import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Progress } from "@/components/ui/progress"
import { ProgressRing } from "./ProgressRing"
import { AnimatedCard } from "./AnimatedCard"
import { CountdownTimer } from "./CountdownTimer"
import { useApexState } from "@/hooks/useApexState"
import { 
  TrendingUp, 
  Zap,
  Crown,
  Target,
  Activity,
  ShoppingBag,
  Users,
  Calendar,
  Star,
  Heart,
  ChevronRight,
  Sparkles,
  Brain,
  Eye,
  TrendingDown
} from "lucide-react"

interface PersonalizedInsight {
  id: string
  type: 'achievement' | 'recommendation' | 'trend' | 'milestone' | 'opportunity'
  title: string
  description: string
  confidence: number // 0-100
  actionable: boolean
  cta?: {
    label: string
    action: string
  }
  metadata?: any
}

interface DashboardWidget {
  id: string
  type: 'stats' | 'progress' | 'social' | 'recommendations' | 'insights'
  title: string
  priority: number
  isPersonalized: boolean
  data: any
}

interface PersonalizedDashboardProps {
  className?: string
}

const personalizedInsights: PersonalizedInsight[] = [
  {
    id: "insight-1",
    type: "milestone",
    title: "🎯 Consistency Streak Building",
    description: "You're on day 4 of your movement streak! Users who reach 7 days are 3x more likely to maintain long-term habits.",
    confidence: 85,
    actionable: true,
    cta: {
      label: "Continue Streak",
      action: "log_movement"
    }
  },
  {
    id: "insight-2", 
    type: "opportunity",
    title: "👑 Architect Tier Unlocked Soon",
    description: "Based on your current engagement pattern, you'll likely reach Architect tier within 2 weeks. One more purchase would accelerate this to 5 days.",
    confidence: 92,
    actionable: true,
    cta: {
      label: "View Path",
      action: "view_tier_progress"
    }
  },
  {
    id: "insight-3",
    type: "recommendation",
    title: "🏃‍♂️ Perfect Training Time",
    description: "Your movement logs show peak performance at 7 AM. The NYC Run Club meets at this time tomorrow - ideal for your schedule!",
    confidence: 78,
    actionable: true,
    cta: {
      label: "Join Event",
      action: "rsvp_event"
    }
  },
  {
    id: "insight-4",
    type: "trend",
    title: "📈 Style Evolution",
    description: "Your preferences have shifted 40% toward architectural aesthetics over the past month. The new Architect Collection aligns perfectly.",
    confidence: 88,
    actionable: false
  }
]

const dashboardWidgets: DashboardWidget[] = [
  {
    id: "stats-overview",
    type: "stats",
    title: "This Week",
    priority: 1,
    isPersonalized: true,
    data: {
      movement: { current: 4, target: 5, change: +2 },
      points: { current: 2840, change: +340 },
      community: { current: 89, change: +12 }
    }
  },
  {
    id: "tier-progress", 
    type: "progress",
    title: "Tier Progress",
    priority: 2,
    isPersonalized: true,
    data: {
      currentTier: "creator",
      nextTier: "architect",
      progress: 68,
      pointsNeeded: 2160,
      timeEstimate: "2 weeks"
    }
  },
  {
    id: "personalized-feed",
    type: "social",
    title: "For You",
    priority: 3,
    isPersonalized: true,
    data: {
      posts: [
        {
          id: "1",
          user: "Maya Rodriguez",
          content: "Just hit a new movement PB!",
          relevanceScore: 95,
          reason: "Similar interests"
        },
        {
          id: "2", 
          user: "Jordan Kim",
          content: "Architect Hoodie styling tips",
          relevanceScore: 87,
          reason: "Matches your style"
        }
      ]
    }
  }
]

export function PersonalizedDashboard({ className }: PersonalizedDashboardProps) {
  const { state } = useApexState()
  const [insights] = useState<PersonalizedInsight[]>(personalizedInsights)
  const [widgets] = useState<DashboardWidget[]>(dashboardWidgets.sort((a, b) => a.priority - b.priority))
  const [timeOfDay, setTimeOfDay] = useState<'morning' | 'afternoon' | 'evening'>('morning')

  // Dynamic greeting based on time
  useEffect(() => {
    const hour = new Date().getHours()
    if (hour < 12) setTimeOfDay('morning')
    else if (hour < 18) setTimeOfDay('afternoon')
    else setTimeOfDay('evening')
  }, [])

  const getGreeting = () => {
    const greetings = {
      morning: "Good morning",
      afternoon: "Good afternoon", 
      evening: "Good evening"
    }
    return greetings[timeOfDay]
  }

  const getInsightIcon = (type: PersonalizedInsight['type']) => {
    switch (type) {
      case 'achievement': return <Trophy size={16} className="text-accent" />
      case 'recommendation': return <Brain size={16} className="text-primary" />
      case 'trend': return <TrendingUp size={16} className="text-blue-400" />
      case 'milestone': return <Target size={16} className="text-green-400" />
      case 'opportunity': return <Sparkles size={16} className="text-purple-400" />
    }
  }

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return "text-green-400"
    if (confidence >= 75) return "text-yellow-400" 
    return "text-orange-400"
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Personalized Greeting */}
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-foreground">
          {getGreeting()}, {state.user?.name?.split(' ')[0] || 'Creator'}
        </h2>
        <p className="text-muted-foreground">
          Ready to shape your world today?
        </p>
      </div>

      {/* AI Insights Panel */}
      <AnimatedCard hoverScale>
        <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Brain size={20} className="text-primary" />
                <h3 className="font-semibold text-foreground">AI Insights</h3>
                <Badge className="bg-primary/20 text-primary text-xs">
                  Powered by AI
                </Badge>
              </div>
              <Button variant="ghost" size="sm">
                <Eye size={16} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {insights.slice(0, 2).map(insight => (
                <div key={insight.id} className="p-4 rounded-lg bg-card/50 border border-white/10">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getInsightIcon(insight.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium text-foreground leading-tight">
                          {insight.title}
                        </h4>
                        <div className={`text-xs ${getConfidenceColor(insight.confidence)}`}>
                          {insight.confidence}% confident
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                        {insight.description}
                      </p>
                      {insight.actionable && insight.cta && (
                        <Button 
                          size="sm" 
                          className="bg-primary hover:bg-primary/90 text-black"
                        >
                          {insight.cta.label}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              <Button variant="outline" className="w-full" size="sm">
                View All Insights ({insights.length})
              </Button>
            </div>
          </CardContent>
        </Card>
      </AnimatedCard>

      {/* Dynamic Stats Grid */}
      <div className="grid grid-cols-2 gap-4">
        <AnimatedCard hoverScale>
          <Card className="bg-card-gradient border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Activity size={16} className="text-accent" />
                  <span className="text-sm font-medium">Movement</span>
                </div>
                <TrendingUp size={14} className="text-green-400" />
              </div>
              <div className="text-center">
                <ProgressRing
                  progress={80}
                  size="md"
                  color="accent"
                >
                  <div className="text-center">
                    <div className="text-lg font-bold text-foreground">4/5</div>
                    <div className="text-xs text-muted-foreground">days</div>
                  </div>
                </ProgressRing>
              </div>
            </CardContent>
          </Card>
        </AnimatedCard>

        <AnimatedCard hoverScale>
          <Card className="bg-card-gradient border-0">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Zap size={16} className="text-primary" />
                  <span className="text-sm font-medium">Points</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-green-400">
                  <TrendingUp size={12} />
                  +340
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary mb-1">2,840</div>
                <div className="text-xs text-muted-foreground">This month</div>
              </div>
            </CardContent>
          </Card>
        </AnimatedCard>
      </div>

      {/* Personalized Quick Actions */}
      <AnimatedCard>
        <Card className="bg-card-gradient border-0">
          <CardHeader>
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-foreground">Recommended Actions</h3>
              <Sparkles size={16} className="text-primary" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <Button 
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2"
              >
                <Activity size={20} className="text-accent" />
                <div className="text-center">
                  <div className="font-medium text-sm">Log Movement</div>
                  <div className="text-xs text-muted-foreground">Continue streak</div>
                </div>
              </Button>
              
              <Button 
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2"
              >
                <Camera size={20} className="text-yellow-500" />
                <div className="text-center">
                  <div className="font-medium text-sm">Join Challenge</div>
                  <div className="text-xs text-muted-foreground">8hrs left</div>
                </div>
              </Button>
              
              <Button 
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2"
              >
                <ShoppingBag size={20} className="text-primary" />
                <div className="text-center">
                  <div className="font-medium text-sm">View Wishlist</div>
                  <div className="text-xs text-muted-foreground">2 items on sale</div>
                </div>
              </Button>
              
              <Button 
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2"
              >
                <Users size={20} className="text-blue-400" />
                <div className="text-center">
                  <div className="font-medium text-sm">NYC Run Club</div>
                  <div className="text-xs text-muted-foreground">Tomorrow 9AM</div>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>
      </AnimatedCard>

      {/* Personalized Content Feed */}
      <AnimatedCard>
        <Card className="bg-card-gradient border-0">
          <CardHeader>
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-foreground">Curated For You</h3>
              <Button variant="ghost" size="sm" className="text-primary">
                <ChevronRight size={16} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/10 hover:bg-muted/20 transition-colors cursor-pointer">
                <Avatar className="w-10 h-10">
                  <AvatarImage src="https://images.unsplash.com/photo-1494790108755-2616b67e1c04?w=40&h=40&fit=crop" />
                  <AvatarFallback>MR</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-foreground text-sm">Maya Rodriguez</h4>
                    <Badge className="tier-badge-architect text-xs">architect</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">Posted movement inspiration</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary" className="text-xs">95% match</Badge>
                    <span className="text-xs text-muted-foreground">Similar interests</span>
                  </div>
                </div>
                <Heart size={16} className="text-muted-foreground" />
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/10 hover:bg-muted/20 transition-colors cursor-pointer">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <ShoppingBag size={16} className="text-primary" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-foreground text-sm">Architect Hoodie</h4>
                  <p className="text-xs text-muted-foreground">Perfect for your style evolution</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary" className="text-xs">87% match</Badge>
                    <span className="text-xs text-primary">Limited stock</span>
                  </div>
                </div>
                <ChevronRight size={16} className="text-muted-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>
      </AnimatedCard>

      {/* Dynamic Progress Tracker */}
      <AnimatedCard>
        <Card className="bg-card-gradient border-0">
          <CardHeader>
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-foreground">Your Progress</h3>
              <Crown size={16} className="text-tier-architect" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Tier Progress</span>
                  <span className="text-sm font-medium">68% to Architect</span>
                </div>
                <Progress value={68} className="h-2 mb-2" />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>2,160 points needed</span>
                  <span>~2 weeks at current pace</span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border">
                <div className="text-center">
                  <div className="text-lg font-bold text-accent">12</div>
                  <div className="text-xs text-muted-foreground">Challenges</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-primary">89</div>
                  <div className="text-xs text-muted-foreground">Community Score</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-secondary">4</div>
                  <div className="text-xs text-muted-foreground">Day Streak</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </AnimatedCard>
    </div>
  )
}